<template>
  <div class="payment-success-page" :dir="isHebrew ? 'rtl' : 'ltr'">
    <title>{{ texts.metaTitle }}</title>
    <SingleRideHeader :presentedText="texts.headerTitle" />
    <b-container>
      <div class="success-content after_page_header">
        <div class="row justify-content-center">
          <div class="col-lg-8 col-md-10">
            <div class="success-card" :class="{ failure: !isSuccessful }">
              <template v-if="isSuccessful">
                <div class="success-icon">
                  <b-icon-check-circle-fill variant="success" font-scale="5"></b-icon-check-circle-fill>
                </div>
                <h1>{{ texts.successTitle }}</h1>
                <p class="success-message">{{ texts.successThanks }}</p>
                <p class="ride-ready-message">
                  {{ texts.successReadyLine1 }}
                  {{ texts.successReadyLine2 }}
                </p>
                <p class="ride-ready-message secondary">
                  {{ texts.successEmailSent }}
                </p>
                <p class="ride-ready-message closing">
                  {{ texts.successClosing }}
                </p>

                <div class="ride-link-card" v-if="rideLink">
                  <p class="ride-link-label">{{ texts.rideLinkLabel }}</p>
                  <p class="ride-link-title">{{ rideTitle || texts.defaultRideTitle }}</p>
                  <a
                    class="ride-link-url"
                    :href="rideLink"
                    target="_blank"
                    rel="noopener"
                    @click.prevent="openRideLink({ focusNewTab: true })"
                  >
                    {{ rideLink }}
                  </a>
                  <b-button
                    variant="success"
                    class="open-ride-btn"
                    @click="openRideLink({ focusNewTab: true })"
                  >
                    {{ texts.openRideButton }}
                  </b-button>
                </div>

                <b-button variant="primary" class="home-btn" @click="goHome">
                  {{ texts.buyMoreButton }}
                </b-button>
              </template>
              <template v-else>
                <div class="failure-icon">
                  <b-icon-exclamation-triangle-fill variant="danger" font-scale="5"></b-icon-exclamation-triangle-fill>
                </div>
                <h1>{{ texts.failureTitle }}</h1>
                <p class="failure-message">
                  {{ texts.failureMessage }}
                </p>
                <p class="failure-reason" v-if="failureReason">
                  {{ texts.failureReasonPrefix }} {{ failureReason }}
                </p>
                <p class="failure-hint">
                  {{ texts.failureHint }}
                </p>
                <div class="failure-actions">
                  <b-button variant="primary" class="retry-btn" @click="retryPayment">
                    {{ texts.retryButton }}
                  </b-button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import { BIconCheckCircleFill, BIconExclamationTriangleFill } from 'bootstrap-vue';
import config from "@/common/config";
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: "PaymentSuccess",
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader,
    BIconCheckCircleFill,
    BIconExclamationTriangleFill
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    texts() {
      return this.getPaymentSuccessTexts();
    }
  },
  data() {
    return {
      paymentResponse: null,
      rideLink: null,
      rideTitle: '',
      isSuccessful: false,
      failureReason: ''
    };
  },
  mounted() {
    // Get response from query params
    const responseParam = this.$route.query.response;
    const sumitResponseParam = this.$route.query.sumitResponse;
    
    if (responseParam) {
      try {
        this.paymentResponse = JSON.parse(responseParam);
        
        // Merge SUMIT response if available
        if (sumitResponseParam) {
          try {
            const sumitResponse = JSON.parse(sumitResponseParam);
            this.paymentResponse = {
              ...this.paymentResponse,
              SUMITResponse: sumitResponse
            };
          } catch (error) {
            console.error('Error parsing SUMIT response:', error);
          }
        }
      } catch (error) {
        console.error('Error parsing payment response:', error);
      }
    } else if (sumitResponseParam) {
      // If only SUMIT response is available
      try {
        this.paymentResponse = {
          SUMITResponse: JSON.parse(sumitResponseParam)
        };
      } catch (error) {
        console.error('Error parsing SUMIT response:', error);
      }
    }
    this.isSuccessful = this.determineSuccess(this.paymentResponse);
    if (this.isSuccessful) {
      this.tryExtractRideLink();
    } else {
      this.failureReason = this.getFailureReason(this.paymentResponse);
    }
  },
  methods: {
    tryExtractRideLink() {
      if (!this.isSuccessful || !this.paymentResponse || this.rideLink) {
        return;
      }
      const rideLinks = Array.isArray(this.paymentResponse.rideLinks)
        ? this.paymentResponse.rideLinks
        : [];
      if (!rideLinks.length) {
        return;
      }
      const firstRide = rideLinks[0] || {};
      if (!firstRide.link) {
        return;
      }
      this.rideLink = firstRide.link;
      this.rideTitle = firstRide.ride_title || firstRide.rideTitle || '';
      // Store rideId for navigation
      if (firstRide.rideId || firstRide.ride_id) {
        this.paymentResponse.rideId = firstRide.rideId || firstRide.ride_id;
      }
    },
    openRideLink(options = {}) {
      if (!this.isSuccessful || !this.rideLink || typeof window === 'undefined') {
        return;
      }
      const newTab = window.open(this.rideLink, '_blank', 'noopener');
      if (options.focusNewTab && newTab) {
        newTab.focus();
      }
    },
    getOwningManager() {
      // Get rideId from route query or payment response
      const rideId = this.$route.query.rideId || 
                     this.paymentResponse?.rideId ||
                     (this.paymentResponse?.rideLinks?.[0]?.rideId);
      
      if (!rideId) {
        return null;
      }
      
      // Find which manager owns this ride
      const featuredPages = config.config.FEATURED_RIDES || [];
      const numericRideId = parseInt(rideId);
      
      return featuredPages.find(page => {
        if (!Array.isArray(page.rides)) {
          return false;
        }
        return page.rides.some(ride => 
          ride.rideId === numericRideId
        );
      });
    },
    goHome() {
      const owningManager = this.getOwningManager();
      
      if (owningManager) {
        // Navigate to the manager's featured rides page
        if (owningManager.slug) {
          this.$router.push({
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: owningManager.slug }
          });
        } else if (owningManager.managerUrlName) {
          this.$router.push({
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: owningManager.managerUrlName }
          });
        } else {
          // Fallback to default
          this.$router.push({ name: 'featured-rides-landing' });
        }
      } else {
        // No manager found, go to default
        this.$router.push({ name: 'featured-rides-landing' });
      }
    },
    retryPayment() {
      this.$router.push({ name: 'payment' });
    },
    determineSuccess(response) {
      if (!response || typeof response !== 'object') {
        return false;
      }
      if (Array.isArray(response.rideLinks) && response.rideLinks.length > 0) {
        return true;
      }
      const sumit = response.sumitResponse || response.sumit_response;
      const payment = sumit?.Data?.Payment;
      if (payment?.ValidPayment) {
        return true;
      }
      const paymentStatus = (payment?.Status || '').toString();
      if (paymentStatus === '000' || paymentStatus === 'Success (0)') {
        return true;
      }
      if (response.ValidPayment || response.Payment?.ValidPayment) {
        return true;
      }
      return false;
    },
    getFailureReason(response) {
      if (!response) {
        return '';
      }
      const sumit = response.sumitResponse || response.sumit_response;
      const payment = sumit?.Data?.Payment;
      return payment?.StatusDescription || response.UserErrorMessage || response.error || '';
    }
  },
  metaInfo() {
    return {
      title: this.texts.metaTitle,
      link: [
        { property: 'og:title', content: this.texts.metaTitle },
        { property: 'og:description', content: this.t('paymentStatusTitle') },
        { property: 'og:type', content: 'website' }
      ]
    };
  }
};
</script>

<style scoped>
.success-content {
  min-height: 60vh;
  padding: 3rem 0;
}

.success-card {
  background: #fff;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.success-card.failure {
  border: 1px solid #f8d7da;
}

.success-icon {
  margin-bottom: 2rem;
  color: #28a745;
}

.failure-icon {
  margin-bottom: 2rem;
  color: #dc3545;
}

.success-card h1 {
  font-size: 2.5rem;
  color: #28a745;
  margin-bottom: 1rem;
}

.success-card.failure h1 {
  color: #dc3545;
}

.success-message {
  font-size: 1.8rem;
  color: #666;
  margin-bottom: 3rem;
}

.failure-message,
.failure-hint {
  font-size: 1.6rem;
  color: #555;
  margin-bottom: 1.5rem;
}

.failure-reason {
  font-size: 1.6rem;
  color: #dc3545;
  font-weight: 600;
  margin-bottom: 1rem;
}

.failure-actions {
  margin-top: 2rem;
}

.retry-btn {
  padding: 1rem 2rem;
  font-size: 1.6rem;
  border-radius: 8px;
  min-width: 200px;
}

.ride-ready-message {
  font-size: 1.6rem;
  color: #0d2f29;
  margin-bottom: 2rem;
  line-height: 1.8;
}

.ride-ready-message.secondary {
  font-size: 1.4rem;
  color: #555;
  margin-top: -1rem;
}

.ride-ready-message.closing {
  font-size: 1.6rem;
  color: #0d2f29;
  margin-top: 1rem;
  font-weight: 600;
}

.ride-link-card {
  background: #e8fff7;
  border: 1px solid #34D0B6;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 3rem;
  text-align: center;
}

.ride-link-label {
  font-size: 1.6rem;
  font-weight: 600;
  color: #0d2f29;
  margin-bottom: 0.5rem;
}

.ride-link-title {
  font-size: 1.8rem;
  color: #07A0C3;
  margin-bottom: 1rem;
}

.ride-link-url {
  display: inline-block;
  font-size: 1.4rem;
  color: #0d2f29;
  word-break: break-all;
  margin-bottom: 1.5rem;
}

.open-ride-btn {
  font-size: 1.6rem;
  padding: 0.8rem 1.5rem;
}

.home-btn {
  padding: 1rem 2rem;
  font-size: 1.6rem;
  border-radius: 8px;
  min-width: 150px;
}

.home-btn {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border: none;
}

.home-btn:hover {
  background: linear-gradient(45deg, #07A0C3, #34D0B6);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

@media (max-width: 768px) {
  .success-card {
    padding: 2rem 1.5rem;
  }

  .failure-message,
  .failure-hint,
  .failure-reason {
    text-align: center;
  }

  .success-card h1 {
    font-size: 2rem;
  }

  .home-btn {
    width: 100%;
  }

  .ride-link-card {
    padding: 1.5rem;
  }

  .retry-btn {
    width: 100%;
  }
}
</style>

